import { ImageWMS } from "ol/source";
import ImageWrapper, { LoadFunction } from "ol/Image";
import { AttributionLike } from "ol/source/Source";
import { ServerType } from "ol/source/wms";
import { ProjectionLike } from "ol/proj";
import axios, { AxiosError } from "axios";

/**
 * ol/source/ImageWMSクラスがコンストラクタで受け取るオプションの型
 */
type ImageWMSOptions = {
  url: string;
  params: Record<string, any>;
  attributions?: AttributionLike;
  crossOrigin?: null | string;
  hidpi?: boolean;
  serverType?: ServerType;
  imageSmoothing?: boolean;
  interpolate?: boolean;
  projection?: ProjectionLike;
  ratio?: number;
  resolutions?: Array<number>;
  imageLoadFunction?: LoadFunction;
};

/**
 * ImageWMSExクラスがコンストラクタで受け取るオプションの型
 * ol/source/ImageWMSクラスからimageLoadFunctionを除いたもの（クラス内で与えるため）
 */
type Options = Omit<ImageWMSOptions, "imageLoadFunction">;

/**
 * オプションのデフォルト値
 * 一部のプロパティはImageWMSクラスと異なるデフォルト値を与える
 */
const defaultOptions: Partial<ImageWMSOptions> = {
  crossOrigin: "Anonymous",
  serverType: "geoserver",
  ratio: 1,
};

/**
 * ImageWMSのラッパークラス
 * 画像のHTTPリクエストにAuthorizationヘッダを自動付加する
 */
class ImageWMSEx extends ImageWMS {
  /**
   * Authorizationヘッダに与えるIDトークンを設定する
   * @param idToken Authorizationヘッダに与える値
   */
  static setIdToken(_idToken: string) {
    return;
  }

  /**
   * Authorizationヘッダを付加して画像をリクエストするためのimageLoadFunction
   * 本メソッドのインタフェースはol/source/ImageWMSで定義されている
   */
  private static loadFunction(image: ImageWrapper, src: string): void {
    const img = image.getImage();
    if ("src" in img) {
      axios
        .get(src, {
          responseType: "arraybuffer",
          headers: {
            "Content-Type": "image/png",
            custom: "viewer",
          },
        })
        .then(function (response) {
          const arrayBufferView = new Uint8Array(response.data);
          const blob = new Blob([arrayBufferView], { type: "image/png" });
          const urlCreator = window.URL || window.webkitURL;
          const imageUrl = urlCreator.createObjectURL(blob);
          img.src = imageUrl;
        })
        .catch((error: AxiosError) => {
          console.error(error);
        });
    }
  }

  /**
   * コンストラクタ
   * @param opt_options オプション imageLoadFunctionが指定できないことを除いてOpenLayersのImageWMSクラスと同じオプションが渡せる
   */
  constructor(opt_options: Options) {
    const options: ImageWMSOptions = {
      ...defaultOptions,
      ...opt_options,
      imageLoadFunction: (image: ImageWrapper, src: string) => {
        if (opt_options.params["LAYERS"]?.length) {
          ImageWMSEx.loadFunction(image, src);
        }
      },
    };
    super(options);
  }
}

export default ImageWMSEx;
